import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Bell, Plus, CheckCircle, Clock } from "@phosphor-icons/react";
import EmptyState from "@/components/EmptyState";
import { CuteLoader } from "@/components/CuteLoader";

export default function AnnouncementsCenter() {
  const { user } = useAuth();
  const location = useLocation();
  const isAdminView = location.pathname.startsWith("/admin");
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [showCompose, setShowCompose] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    audience: "all",
    priority: "normal"
  });

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    setLoading(true);
    try {
      const endpoint = isAdminView
        ? "/announcements/admin/history" 
        : "/announcements";
      const { data } = await api.get(endpoint);
      setAnnouncements(data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await api.post("/announcements", {
        ...formData,
        author_id: user._id,
        author_name: user.display_name || user.name
      });
      setShowCompose(false);
      setFormData({ title: "", content: "", audience: "all", priority: "normal" });
      loadAnnouncements();
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleDelete = async (announcementId) => {
    if (!window.confirm("Delete this announcement?")) return;
    
    try {
      await api.delete(`/announcements/${announcementId}`);
      loadAnnouncements();
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleAcknowledge = async (announcementId) => {
    try {
      await api.post(`/announcements/${announcementId}/acknowledge`, {
        user_id: user._id,
        acknowledged_at: new Date().toISOString(),
      });
      loadAnnouncements();
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent": return "destructive";
      case "high": return "default";
      default: return "secondary";
    }
  };

  if (loading) {
    return <CuteLoader />;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-foreground flex items-center gap-2">
              <Bell className="h-6 w-6" />
              Announcements
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isAdminView ? "Full announcement history with acknowledgment tracking" : "Team updates and news"}
            </p>
          </div>
          {isAdminView && (
            <Button onClick={() => setShowCompose(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Announcement
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 space-y-4">
        {announcements.length === 0 ? (
          <Card>
            <CardContent className="py-4">
              <EmptyState
                testId="announcements-empty"
                line="Nothing new right now"
                sub="You're all caught up, love. New team notes will land here."
                icon={<Bell className="h-10 w-10" />}
              />
            </CardContent>
          </Card>
        ) : (
          announcements.map((ann) => (
            <Card key={ann._id || ann.id} className="hover:border-primary/50 transition-colors">
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <CardTitle className="text-lg">{ann.title}</CardTitle>
                      <Badge variant={getPriorityColor(ann.priority)}>
                        {ann.priority || "normal"}
                      </Badge>
                    </div>
                    <CardDescription>
                      By {ann.author_name} • {new Date(ann.created_at).toLocaleDateString('en-US', { timeZone: 'UTC' })} at {new Date(ann.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}
                    </CardDescription>
                  </div>
                  {isAdminView && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDelete(ann._id)}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-foreground whitespace-pre-wrap mb-4">{ann.content}</p>

                {/* Employee view: acknowledge control */}
                {!isAdminView && (
                  ann.acknowledged ? (
                    <div className="flex items-center gap-2 text-sm text-green-700" data-testid={`ann-acked-${ann.id}`}>
                      <CheckCircle className="h-4 w-4" /> You've seen this
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => handleAcknowledge(ann.id)} data-testid={`ann-ack-btn-${ann.id}`}>
                      Got it
                    </Button>
                  )
                )}

                {/* Admin view: Acknowledgment stats */}
                {isAdminView && ann.acknowledgment_count !== undefined && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-muted-foreground">
                          {ann.acknowledgment_count} / {ann.total_eligible} seen ({ann.acknowledgment_rate}%)
                        </span>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {ann.audience || "all"}
                      </Badge>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Compose Dialog */}
      <Dialog open={showCompose} onOpenChange={setShowCompose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Announcement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Title</Label>
              <Input 
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Announcement title"
              />
            </div>
            <div>
              <Label>Content</Label>
              <Textarea 
                rows={6}
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Write your announcement here..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Audience</Label>
                <Select value={formData.audience} onValueChange={(val) => setFormData(prev => ({ ...prev, audience: val }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Staff</SelectItem>
                    <SelectItem value="employees">Employees Only</SelectItem>
                    <SelectItem value="managers">Managers Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(val) => setFormData(prev => ({ ...prev, priority: val }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 pt-4">
              <Button onClick={handleCreate} disabled={!formData.title || !formData.content}>
                Publish Announcement
              </Button>
              <Button onClick={() => setShowCompose(false)} variant="outline">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
